import * as Select from '@radix-ui/react-select';
import type { ReactNode } from 'react';
import {
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DropdownMenu } from '~/chords/DropdownSingleSelect/components/DropdownMenu';
import { DropdownTrigger } from '~/chords/DropdownSingleSelect/components/DropdownTrigger';
import { DROPDOWN_CLEAR_SENTINEL_VALUE } from '~/chords/DropdownSingleSelect/constants';
import { useTriggerZIndex } from '~/chords/DropdownSingleSelect/hooks/useTriggerZIndex';
import type {
  DropdownContentOptions,
  DropdownSearchOptions,
  DropdownState,
  ExternalDropdownOptions,
  HeadingTemplateComponent,
  InternalDropdownOption,
  InternalDropdownOptions,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { updateExternalToInternal } from '~/chords/DropdownSingleSelect/utils/updateExternalToInternal';
import { updateIsVisibleSearch } from '~/chords/DropdownSingleSelect/utils/updateIsVisibleSearch';
import { updateIsVisibleTrue } from '~/chords/DropdownSingleSelect/utils/updateIsVisibleTrue';
import { updateSelectedOptions } from '~/chords/DropdownSingleSelect/utils/updateSelectedOptions';

export interface DropdownSingleSelectProps<TOptionProps = unknown>
  extends Select.SelectProps {
  id?: string;
  placeholder?: string;
  options: ExternalDropdownOptions<TOptionProps>;
  contentOptions?: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  showCheckIcon?: boolean;
  state?: DropdownState;

  // When true, ignores close requests resulting from blur (e.g. switching tabs).
  // Other closes (Escape, selection, click outside) still apply.
  preventCloseOnWindowBlur?: boolean;

  // Provide an optional React component to show the heading for grouped options with custom styling.
  HeadingTemplate?: HeadingTemplateComponent;

  // Provide an optional React component to show options with custom styling.
  // For instance, display the country flag next to the country name.
  OptionTemplate?: OptionTemplateComponent;

  // Replace the default select-box trigger with any element.
  renderTrigger?: (props: {
    isOpen: boolean;
    ref: (element: HTMLButtonElement | null) => void;
  }) => ReactNode;

  // When true, shows a clear control in the trigger (on hover) and a pinned "Clear selection"
  // row at the top of the open menu whenever a value is selected. Requires onClear to be
  // provided as well; without it the clear UI is suppressed since clearing has no effect
  // on the consumer's controlled value.
  isClearable?: boolean;

  // Called when the user clears the selection. The consumer is responsible for resetting
  // the controlled value (e.g. call setValue('') in their form).
  onClear?: () => void;
}

/**
 * DropdownSingleSelect is built on top of Radix UI's Select and Scroll Area primitives.
 * https://www.radix-ui.com/docs/primitives/components/select
 * https://www.radix-ui.com/docs/primitives/components/scroll-area
 */
export function DropdownSingleSelect<TOptionProps = unknown>({
  id,
  placeholder,
  options: externalOptions,
  contentOptions,
  searchOptions,
  showCheckIcon,
  state,
  HeadingTemplate,
  OptionTemplate,
  disabled,
  required,
  onOpenChange: parentOnOpenChange,
  preventCloseOnWindowBlur,
  renderTrigger,
  isClearable,
  onClear,
  ...select
}: DropdownSingleSelectProps<TOptionProps>) {
  const initialSelectedValue = select?.value ?? select?.defaultValue;
  const [initialResult] = useState(() =>
    updateExternalToInternal(externalOptions, {
      selectedValue: initialSelectedValue,
    }),
  );

  const [selectedOption, setSelectedOption] = useState<
    InternalDropdownOption | undefined
  >(initialResult.selectedOption);

  const [options, setOptions] = useState<InternalDropdownOptions>(
    initialResult.internalOptions,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryTrimmed = searchQuery.trim();
  const searchQueryRef = useRef(searchQuery);
  const [isOpen, setIsOpen] = useState(
    select?.open || select?.defaultOpen || false,
  );
  const isOpenRef = useRef(isOpen);
  const [triggerRefCallback, triggerZIndex] = useTriggerZIndex(
    contentOptions?.zIndex,
  );
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);

  /**
   * Keep selectedValue in sync with select.value and select.defaultValue.
   */
  useLayoutEffect(() => {
    setSelectedValue(select?.value ?? select?.defaultValue);
  }, [select?.value, select?.defaultValue]);

  /**
   * When the dropdown is closed, clear the search query.
   */
  useEffect(() => {
    if (!isOpen && isOpenRef.current && searchQueryTrimmed) {
      setSearchQuery('');
    }
  }, [isOpen, searchQueryTrimmed]);

  /**
   * Remember the previous isOpen state so that we're only running certain useEffects
   * when isOpen changes and not when any of the other dependencies change.
   */
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * Translate the barebones external options object and augment it with the data structure we use
   * internally to manage the dropdown state (options, selection, and visibility).
   */
  const externalDerived = useMemo(
    () => updateExternalToInternal(externalOptions, { selectedValue }),
    [externalOptions, selectedValue],
  );

  const prevExternalDerivedRef = useRef(externalDerived);

  if (externalDerived !== prevExternalDerivedRef.current) {
    prevExternalDerivedRef.current = externalDerived;
    setOptions(externalDerived.internalOptions);
    setSelectedOption(externalDerived.selectedOption);
  }

  /**
   * When the searchQuery changes, update the options to show only the options
   * that match the search query. Skip if internal filtering is disabled.
   */
  useEffect(() => {
    if (searchOptions?.disableInternalFiltering) {
      return;
    }

    // If the search query hasn't changed, do nothing.
    if (searchQueryTrimmed === searchQueryRef.current) {
      return;
    }

    if (searchQueryTrimmed) {
      setOptions((prevOptions) =>
        updateIsVisibleSearch(prevOptions, {
          searchQueryTrimmed,
        }),
      );

      return;
    }

    setOptions(updateIsVisibleTrue);
  }, [searchQueryTrimmed, searchOptions?.disableInternalFiltering]);

  /**
   * Remember the previous searchQuery value so that we're only running certain useEffects
   * when the searchQuery changes.
   */
  useEffect(() => {
    searchQueryRef.current = searchQueryTrimmed;
  }, [searchQueryTrimmed]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    searchOptions?.onChange?.(query);
  };

  const handleSelectOption = (newSelectedOption: InternalDropdownOption) => {
    setSelectedOption((prevSelectedOption) => {
      // If this option is already the selected option, do nothing.
      if (prevSelectedOption?.value === newSelectedOption.value) {
        return prevSelectedOption;
      }

      // Update options based on previous selected option.
      setOptions((prevOptions) =>
        updateSelectedOptions(prevOptions, {
          prevSelectedOptionIndex: prevSelectedOption?.optionIndex,
          newSelectedOptionIndex: newSelectedOption.optionIndex,
        }),
      );

      return newSelectedOption;
    });
  };

  const handleClear = () => {
    setOptions((prevOptions) =>
      updateSelectedOptions(prevOptions, {
        prevSelectedOptionIndex: selectedOption?.optionIndex,
        newSelectedOptionIndex: undefined,
      }),
    );
    setSelectedOption(undefined);
    setSelectedValue(undefined);
    startTransition(() => setIsOpen(false));

    if (isOpenRef.current) {
      parentOnOpenChange?.(false);
    }

    onClear?.();
  };

  // The clear UI is only meaningful when the consumer can react to clearing
  // (otherwise the controlled value snaps back and the click does nothing
  // visible). Gate both the trigger overlay and the pinned menu row on onClear.
  const isClearableEnabled = Boolean(isClearable && onClear && !disabled);
  const radixWithSelect: RadixUiProps['radix'] = {};

  return (
    <Select.Root
      {...select}
      open={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen && preventCloseOnWindowBlur && !document.hasFocus()) {
          return;
        }

        startTransition(() => {
          setIsOpen(newIsOpen);
        });
        parentOnOpenChange?.(newIsOpen);
      }}
      onValueChange={(value) => {
        // Radix guarantees (and throws an error) that '' isn't a valid value.
        // The only way this can happen is if we have two DropdownSingleSelects
        // that are being used on the same form referencing the same field. This
        // prevents onValueChange('') from firing which ends up overriding the
        // selected value in the form. For a much more detailed explanation,
        // see WMG1-1078.
        if (value === '') {
          return;
        }

        // The pinned "Clear selection" row is rendered as a Select.Item with a
        // sentinel value so it joins Radix's roving focus / typeahead. Route
        // it through handleClear and never propagate the sentinel to consumers.
        if (value === DROPDOWN_CLEAR_SENTINEL_VALUE) {
          handleClear();

          return;
        }

        select.onValueChange?.(value);
      }}
      disabled={disabled}
      required={required}
    >
      {renderTrigger ? (
        <Select.Trigger asChild id={id} aria-invalid={state === 'error'}>
          {renderTrigger({ isOpen, ref: triggerRefCallback })}
        </Select.Trigger>
      ) : (
        <DropdownTrigger
          ref={triggerRefCallback}
          id={id}
          placeholder={placeholder}
          selectedOption={selectedOption}
          isOpen={isOpen}
          state={state}
          disabled={disabled}
          OptionTemplate={OptionTemplate}
          isClearable={isClearableEnabled}
          onClear={handleClear}
          radix={radixWithSelect}
        />
      )}
      {isOpen ? (
        <DropdownMenu
          options={options}
          contentOptions={{
            ...contentOptions,
            zIndex: triggerZIndex,
          }}
          searchOptions={searchOptions}
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          selectedOption={selectedOption}
          onSelectOption={handleSelectOption}
          showCheckIcon={showCheckIcon}
          HeadingTemplate={HeadingTemplate}
          OptionTemplate={OptionTemplate}
          isClearable={isClearableEnabled}
          radix={radixWithSelect}
        />
      ) : null}
    </Select.Root>
  );
}

DropdownSingleSelect.displayName = 'DropdownSingleSelect';
